if (!window.Eurus.loadedScript.includes('product-comparison.js')) {
  window.Eurus.loadedScript.push('product-comparison.js');
  
  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('compareModal', () => ({
        open: false,
        compareProducts: [],
        addingToCart: {},
        selectedVariants: {},
        selectedVariantOptions: {},
        searchQuery: '',
        selectedGridFilter: '',
        gridStep: 15,
        visibleGridCount: 15,
        sectionId: '',
        currentGridPage: 1,
        nextGridPage: null,
        hasNextGridPage: false,
        isLoadingGridPage: false,
        activeGridMode: 'browse',
        defaultGridState: null,
        activeSearchTerm: '',
        searchRequestKey: 0,
        filterRequestKey: 0,
        filterPrefetchKey: 0,
        filterCollectionHandles: {
          terracotta: 'terracotta-tiles',
          cement: 'cement-tiles',
          zellige: 'zellige-tiles',
          glaze: 'glaze-tiles',
          porcelain: 'porcelain-tiles-collection'
        },
        gridCardMarkupCache: {},

        init() {
          this.syncGridPaginationMeta();
          this.saveDefaultGridState();
          this.syncGridCardMarkupCache();

          try {
            const storedProducts = localStorage.getItem('compareProducts');
            if (storedProducts) {
              const parsedProducts = JSON.parse(storedProducts);
              if (Array.isArray(parsedProducts)) {
                this.compareProducts = parsedProducts
                  .filter((item) => item && typeof item === 'object')
                  .filter((item) => item.handle || item.id || item.variant_id || item.variantId)
                  .slice(0, 3);
              }
            }
            this.selectedVariants = {};
            this.selectedVariantOptions = {};
          } catch (error) {
            console.error('Unable to load compare products', error);
          }

          this.$watch('compareProducts', (products) => {
            localStorage.setItem('compareProducts', JSON.stringify(products.slice(0, 3)));
            this.selectedVariants = {};
            this.selectedVariantOptions = {};
          });

          this.$watch('searchQuery', () => {
            this.visibleGridCount = this.gridStep;
            this.handleSearchQueryChange();
          });

          this.openFromTrigger = (event) => {
            if (!event.target.closest('.open-compare')) return;
            event.preventDefault();
            this.openModal();
          };

          this.updateOverlayPanelHeight = () => {
            const stickyHeader = document.querySelector('#sticky-header');
            const announcement = document.querySelector('.section-announcement');
            const stickyHeaderHeight = stickyHeader ? stickyHeader.offsetHeight : 0;
            const announcementHeight = announcement ? announcement.offsetHeight : 0;
            const offset = stickyHeaderHeight + announcementHeight;

            if (this.$root) {
              this.$root.style.setProperty('--compare-overlay-offset', `${offset}px`);
            }
          };

          document.addEventListener('click', this.openFromTrigger);
          window.addEventListener('resize', this.updateOverlayPanelHeight);
        },

        syncGridPaginationMeta(sourceRoot = this.$root) {
          if (!sourceRoot) return;

          this.sectionId = sourceRoot.dataset.sectionId || this.sectionId || '';

          const grid = sourceRoot.querySelector('.compare-grid');
          if (!grid) return;

          const currentPage = parseInt(grid.dataset.gridPage || '1', 10);
          const nextPage = parseInt(grid.dataset.gridNextPage || '', 10);

          this.currentGridPage = Number.isNaN(currentPage) ? 1 : currentPage;
          this.nextGridPage = Number.isNaN(nextPage) ? null : nextPage;
          this.hasNextGridPage = grid.dataset.gridHasNextPage === 'true';
        },

        getGridContainer(root = this.$root) {
          if (!root) return null;
          return root.querySelector('.compare-grid');
        },

        captureGridState(grid = this.getGridContainer()) {
          if (!grid) return null;

          return {
            innerHTML: grid.innerHTML,
            page: grid.dataset.gridPage || '1',
            nextPage: grid.dataset.gridNextPage || '',
            hasNextPage: grid.dataset.gridHasNextPage || 'false',
            isSearchResults: grid.dataset.gridIsSearchResults || 'false'
          };
        },

        applyGridState(state) {
          const grid = this.getGridContainer();
          if (!grid || !state) return;

          grid.innerHTML = state.innerHTML || '';
          grid.dataset.gridPage = state.page || '1';
          grid.dataset.gridNextPage = state.nextPage || '';
          grid.dataset.gridHasNextPage = state.hasNextPage || 'false';
          grid.dataset.gridIsSearchResults = state.isSearchResults || 'false';

          if (window.Alpine && typeof window.Alpine.initTree === 'function') {
            window.Alpine.initTree(grid);
          }

          this.syncGridPaginationMeta();
          this.syncGridCardMarkupCache(grid);
        },

        saveDefaultGridState() {
          if (this.activeGridMode !== 'browse') return;

          const state = this.captureGridState();
          if (!state) return;

          this.defaultGridState = state;
        },

        restoreDefaultGridState() {
          if (!this.defaultGridState) return;

          this.activeGridMode = 'browse';
          this.applyGridState(this.defaultGridState);
        },

        syncGridCardMarkupCache(root = this.getGridContainer()) {
          if (!root) return;

          const nextCache = { ...this.gridCardMarkupCache };
          Array.from(root.querySelectorAll('.compare-grid-card[data-compare-handle]')).forEach((card) => {
            const handle = card.dataset.compareHandle;
            const renderedCard = card.querySelector('.card-product');
            if (!handle || !renderedCard) return;
            nextCache[handle] = renderedCard.outerHTML;
          });

          this.gridCardMarkupCache = nextCache;
        },

        buildGridPageUrl(page) {
          if (!this.sectionId || !page) return null;

          const url = new URL(window.location.href);
          url.searchParams.delete('section_id');
          url.searchParams.set('sections', this.sectionId);
          url.searchParams.set('page', String(page));
          return url.toString();
        },

        getCollectionHandleForFilter(filterKey = this.selectedGridFilter) {
          const normalizedFilter = (filterKey || '').toString().trim().toLowerCase();
          if (!normalizedFilter) return '';

          return this.filterCollectionHandles[normalizedFilter] || '';
        },

        buildCollectionFilterUrl(filterKey, page = 1) {
          const collectionHandle = this.getCollectionHandleForFilter(filterKey);
          if (!collectionHandle) return null;

          const url = new URL(window.Shopify.routes.root + 'collections/' + collectionHandle, window.location.origin);
          url.searchParams.set('view', 'compare_grid_data');
          url.searchParams.set('page', String(page));
          return url.toString();
        },

        appendGridCards(nextGrid) {
          const currentGrid = this.getGridContainer();
          if (!currentGrid || !nextGrid) return 0;

          const existingHandles = new Set(
            Array.from(currentGrid.querySelectorAll('.compare-grid-card[data-compare-handle]'))
              .map((card) => card.dataset.compareHandle)
              .filter(Boolean)
          );

          let appendedCount = 0;

          Array.from(nextGrid.querySelectorAll('.compare-grid-card[data-compare-handle]')).forEach((card) => {
            const handle = card.dataset.compareHandle;
            if (handle && existingHandles.has(handle)) return;

            const clonedCard = card.cloneNode(true);
            currentGrid.appendChild(clonedCard);
            existingHandles.add(handle);
            appendedCount += 1;

            if (window.Alpine && typeof window.Alpine.initTree === 'function') {
              window.Alpine.initTree(clonedCard);
            }
          });

          currentGrid.dataset.gridPage = nextGrid.dataset.gridPage || currentGrid.dataset.gridPage || '';
          currentGrid.dataset.gridNextPage = nextGrid.dataset.gridNextPage || '';
          currentGrid.dataset.gridHasNextPage = nextGrid.dataset.gridHasNextPage || 'false';
          currentGrid.dataset.gridIsSearchResults = nextGrid.dataset.gridIsSearchResults || currentGrid.dataset.gridIsSearchResults || 'false';
          this.syncGridPaginationMeta();
          this.syncGridCardMarkupCache(currentGrid);

          if (this.activeGridMode === 'browse') {
            this.saveDefaultGridState();
          }

          return appendedCount;
        },

        replaceGridCards(nextGrid, mode = this.activeGridMode) {
          const currentGrid = this.getGridContainer();
          if (!currentGrid || !nextGrid) return 0;

          currentGrid.innerHTML = nextGrid.innerHTML;
          currentGrid.dataset.gridPage = nextGrid.dataset.gridPage || '1';
          currentGrid.dataset.gridNextPage = nextGrid.dataset.gridNextPage || '';
          currentGrid.dataset.gridHasNextPage = nextGrid.dataset.gridHasNextPage || 'false';
          currentGrid.dataset.gridIsSearchResults = nextGrid.dataset.gridIsSearchResults || (mode === 'search' ? 'true' : 'false');

          if (window.Alpine && typeof window.Alpine.initTree === 'function') {
            window.Alpine.initTree(currentGrid);
          }

          this.activeGridMode = mode;
          this.syncGridPaginationMeta();
          this.syncGridCardMarkupCache(currentGrid);

          return currentGrid.querySelectorAll('.compare-grid-card[data-compare-handle]').length;
        },

        fetchGridPage(page) {
          const url = this.buildGridPageUrl(page);
          if (!url) return Promise.resolve(0);

          this.isLoadingGridPage = true;

          return fetch(url, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          })
            .then((response) => response.ok ? response.json() : null)
            .then((sections) => {
              const sectionHtml = sections && this.sectionId ? sections[this.sectionId] : '';
              if (!sectionHtml) return 0;

              const parsedSection = document.createElement('div');
              parsedSection.innerHTML = sectionHtml;
              const nextGrid = parsedSection.querySelector('.compare-grid');
              if (!nextGrid) return 0;

              return this.appendGridCards(nextGrid);
            })
            .catch((error) => {
              console.error('Error loading compare grid page:', error);
              return 0;
            })
            .finally(() => {
              this.isLoadingGridPage = false;
            });
        },

        buildSearchUrl(page = 1) {
          const query = (this.searchQuery || '').trim();
          if (!query) return null;

          const url = new URL(window.Shopify.routes.root + 'search', window.location.origin);
          url.searchParams.set('view', 'compare_grid_data');
          url.searchParams.set('q', query);
          url.searchParams.set('type', 'product');
          url.searchParams.set('options[prefix]', 'last');
          url.searchParams.set('page', String(page));
          return url.toString();
        },

        fetchSearchGrid(page = 1, append = false) {
          const url = this.buildSearchUrl(page);
          if (!url) return Promise.resolve(0);

          const requestKey = ++this.searchRequestKey;
          this.isLoadingGridPage = true;

          return fetch(url, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          })
            .then((response) => response.ok ? response.text() : '')
            .then((markup) => {
              if (requestKey !== this.searchRequestKey) return 0;

              const parsed = document.createElement('div');
              parsed.innerHTML = markup;
              const nextGrid = parsed.querySelector('.compare-grid');
              if (!nextGrid) return 0;

              this.activeGridMode = 'search';
              this.activeSearchTerm = (this.searchQuery || '').trim();

              if (append) {
                return this.appendGridCards(nextGrid);
              }

              return this.replaceGridCards(nextGrid, 'search');
            })
            .catch((error) => {
              console.error('Error loading compare search results:', error);
              return 0;
            })
            .finally(() => {
              if (requestKey === this.searchRequestKey) {
                this.isLoadingGridPage = false;
              }
            });
        },

        fetchCollectionFilterGrid(filterKey, page = 1, append = false) {
          const url = this.buildCollectionFilterUrl(filterKey, page);
          if (!url) return Promise.resolve(0);

          const requestKey = ++this.filterRequestKey;
          this.isLoadingGridPage = true;

          return fetch(url, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          })
            .then((response) => response.ok ? response.text() : '')
            .then((markup) => {
              if (requestKey !== this.filterRequestKey) return 0;

              const parsed = document.createElement('div');
              parsed.innerHTML = markup;
              const nextGrid = parsed.querySelector('.compare-grid');
              if (!nextGrid) return 0;

              if (append) {
                this.activeGridMode = 'filter';
                return this.appendGridCards(nextGrid);
              }

              return this.replaceGridCards(nextGrid, 'filter');
            })
            .catch((error) => {
              console.error('Error loading compare filter results:', error);
              return 0;
            })
            .finally(() => {
              if (requestKey === this.filterRequestKey) {
                this.isLoadingGridPage = false;
              }
            });
        },

        handleSearchQueryChange() {
          this.filterPrefetchKey += 1;
          const query = (this.searchQuery || '').trim();

          if (!query) {
            this.searchRequestKey += 1;
            this.filterRequestKey += 1;
            this.activeSearchTerm = '';
            this.isLoadingGridPage = false;
            if ((this.selectedGridFilter || '').trim()) {
              this.fetchCollectionFilterGrid(this.selectedGridFilter, 1, false);
              return;
            }

            this.restoreDefaultGridState();
            return;
          }

          if (this.activeGridMode !== 'search') {
            this.saveDefaultGridState();
          }

          this.fetchSearchGrid(1, false)
            .then(() => {
              if ((this.selectedGridFilter || '').trim()) {
                this.ensureFilterCoverage();
              }
            });
        },

        destroy() {
          if (this.openFromTrigger) {
            document.removeEventListener('click', this.openFromTrigger);
          }
          if (this.updateOverlayPanelHeight) {
            window.removeEventListener('resize', this.updateOverlayPanelHeight);
          }
          document.body.style.overflow = '';
        },

        openModal() {
          if (this.updateOverlayPanelHeight) {
            this.updateOverlayPanelHeight();
          }
          window.scrollTo({ top: 0, behavior: 'auto' });
          this.open = true;
          document.body.style.overflow = 'hidden';
        },

        closeModal() {
          this.open = false;
          document.body.style.overflow = '';
        },

        addProductByHandle(handle) {
          if (!handle || this.isAdded(handle)) return;
          if (this.compareProducts.length >= 3) return;

          // Fetch product data from Shopify
          fetch(window.Shopify.routes.root + 'products/' + handle + '.js')
            .then((response) => response.ok ? response.json() : null)
            .then((productData) => {
              if (!productData) return;
              
              console.log('Product data:', productData);
              
              // Get image URL from images array
              let imageUrl = '';
              let imageList = [];
              if (productData.images && productData.images.length > 0) {
                imageUrl = productData.images[0];
                imageList = productData.images.map((image) => {
                  if (typeof image === 'string') {
                    return image + '?width=600&height=600&crop=center';
                  }
                  if (image && image.src) {
                    return image.src + '?width=600&height=600&crop=center';
                  }
                  return '';
                }).filter(Boolean);
                console.log('First image from images array:', imageUrl);
                
                // Add size parameters if it's a string URL
                if (typeof imageUrl === 'string') {
                  imageUrl = imageUrl + '?width=600&height=600&crop=center';
                } else if (imageUrl && imageUrl.src) {
                  // If it's an object with src property
                  imageUrl = imageUrl.src + '?width=600&height=600&crop=center';
                }
              }
              
              console.log('Final Image URL:', imageUrl);
              
              const product = {
                handle: handle,
                url: productData.url || (window.Shopify.routes.root + 'products/' + handle),
                title: productData.title,
                image: imageUrl,
                images: imageList,
                price: productData.price ? this.formatPrice(productData.price) : '',
                price_raw: productData.price || 0,
                id: productData.variants && productData.variants[0] ? productData.variants[0].id : null,
                rating_value: null,
                rating_count: null,
                variants: productData.variants || []
              };
              
              console.log('Product object:', product);
              this.addProduct(product);
            })
            .catch((error) => {
              console.error('Error fetching product by handle:', error);
            });
        },

        formatPrice(price) {
          // Simple price formatting - adjust based on your currency settings
          const cents = price % 100;
          const dollars = Math.floor(price / 100);
          return '$' + dollars + '.' + (cents < 10 ? '0' : '') + cents;
        },

        addProduct(product) {
          if (!product || !product.handle) return;
          if (this.isAdded(product.handle)) return;
          if (this.compareProducts.length >= 3) return;
          
          // Add product immediately so slot shows
          if (!Array.isArray(product.images)) {
            product.images = product.image ? [product.image] : [];
          }
          product.variants = [];
          this.compareProducts.push(product);
          
          // Fetch full product data including variants
          fetch(window.Shopify.routes.root + 'products/' + product.handle + '.js')
            .then((response) => response.ok ? response.json() : null)
            .then((productData) => {
              if (productData) {
                // Find the product in array and update with variants
                const productIndex = this.compareProducts.findIndex((p) => p.handle === product.handle);
                if (productIndex >= 0) {
                  const nextImages = Array.isArray(productData.images)
                    ? productData.images.map((image) => {
                      if (typeof image === 'string') {
                        return image + '?width=600&height=600&crop=center';
                      }
                      if (image && image.src) {
                        return image.src + '?width=600&height=600&crop=center';
                      }
                      return '';
                    }).filter(Boolean)
                    : [];

                  this.compareProducts[productIndex].images = nextImages.length
                    ? nextImages
                    : (this.compareProducts[productIndex].image ? [this.compareProducts[productIndex].image] : []);

                  this.compareProducts[productIndex].variants = Array.isArray(productData.variants)
                    ? productData.variants
                    : [];
                  this.ensureVariantSelection(productIndex);
                }
              }
            })
            .catch((error) => {
              console.error('Error fetching product variants:', error);
            });
        },

        removeProduct(index) {
          if (index < 0 || index > this.compareProducts.length - 1) return;
          const product = this.compareProducts[index];
          if (product && product.handle) {
            delete this.selectedVariants[product.handle];
            delete this.selectedVariantOptions[product.handle];
          }
          this.compareProducts = this.compareProducts.filter((_, itemIndex) => itemIndex !== index);
        },

        selectVariant(slotIndex, variantId) {
          const slot = this.compareProducts[slotIndex];
          if (!slot || !slot.handle) return;
          const variant = Array.isArray(slot.variants)
            ? slot.variants.find((item) => Number(item.id) === Number(variantId))
            : null;

          if (variant) {
            const parsed = this.parseVariantTitle(variant.title);
            const current = this.selectedVariantOptions[slot.handle] || { color: '', quantity: '' };
            this.selectedVariantOptions = {
              ...this.selectedVariantOptions,
              [slot.handle]: {
                ...current,
                color: parsed.color,
                quantity: parsed.quantity
              }
            };
          }

          this.selectedVariants = {
            ...this.selectedVariants,
            [slot.handle]: Number(variantId)
          };
        },

        parseVariantTitle(title) {
          const value = (title || '').toString();
          const parts = value.split(' / ');
          const color = (parts[0] || '').trim();
          const quantity = (parts.slice(1).join(' / ') || '').trim();

          return { color, quantity };
        },

        getColorSwatchStyle(slotIndex, color) {
          const rawColor = (color || '').toString().trim();
          const normalized = rawColor.toLowerCase();
          const variants = this.getParsedVariants(slotIndex);
          const colorVariant = variants.find((item) => item.color === color) || null;
          const variantImage = colorVariant && colorVariant.featured_image
            ? (typeof colorVariant.featured_image === 'string'
              ? colorVariant.featured_image
              : colorVariant.featured_image.src)
            : '';

          if (variantImage) {
            const smallImage = `${variantImage}&width=76`;
            return `--bg-image: url(${smallImage}); background: #f3f1ec;`;
          }

          const presetColors = {
            black: '#111111',
            white: '#ffffff',
            ivory: '#fffff0',
            cream: '#f8f4e8',
            beige: '#d9c7a3',
            tan: '#b79062',
            brown: '#7a4e2b',
            grey: '#8b8b8b',
            gray: '#8b8b8b',
            red: '#c0392b',
            maroon: '#6e1f29',
            orange: '#ef8a17',
            yellow: '#f2c94c',
            green: '#3f7d43',
            olive: '#65753f',
            mint: '#7ec8b3',
            teal: '#0f7b75',
            blue: '#2f66c7',
            navy: '#1f2a5a',
            purple: '#6d4fa6',
            pink: '#e89ab9'
          };

          const isHex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(rawColor);
          const isRgb = /^rgba?\([0-9\s.,%]+\)$/i.test(rawColor);
          const isHsl = /^hsla?\([0-9\s.,%]+\)$/i.test(rawColor);
          const isNamedColor = /^[a-z\s-]+$/i.test(rawColor);

          if (/\/|,|multi|assorted|mix|combo|various|rainbow/i.test(normalized)) {
            return '--bg-image: linear-gradient(135deg, #ff6b6b 0%, #ffd166 25%, #4ecdc4 50%, #5f6cf0 75%, #e879f9 100%); background: #ffffff;';
          }

          if (isHex || isRgb || isHsl) {
            return `--bg-image: none; background: ${rawColor};`;
          }

          if (presetColors[normalized]) {
            return `--bg-image: none; background: ${presetColors[normalized]};`;
          }

          if (isNamedColor) {
            return `--bg-image: none; background: ${normalized};`;
          }

          return '--bg-image: none; background: #f3f1ec;';
        },

        getParsedVariants(slotIndex) {
          const slot = this.compareProducts[slotIndex];
          if (!slot || !Array.isArray(slot.variants)) return [];

          return slot.variants
            .map((variant) => {
              const parsed = this.parseVariantTitle(variant.title);
              return {
                ...variant,
                color: parsed.color,
                quantity: parsed.quantity
              };
            })
            .filter((variant) => variant.color && variant.quantity);
        },

        getVariantColors(slotIndex) {
          const variants = this.getParsedVariants(slotIndex);
          return [...new Set(variants.map((variant) => variant.color))];
        },

        getVariantQuantities(slotIndex) {
          const variants = this.getParsedVariants(slotIndex);
          return [...new Set(variants.map((variant) => variant.quantity))];
        },

        findVariantByOptions(slotIndex, color, quantity) {
          const variants = this.getParsedVariants(slotIndex);
          return variants.find((variant) => variant.color === color && variant.quantity === quantity) || null;
        },

        getSelectionState(slotIndex) {
          const slot = this.compareProducts[slotIndex];
          if (!slot || !slot.handle) return { color: '', quantity: '' };

          const current = this.selectedVariantOptions[slot.handle];
          if (current) return current;

          this.selectedVariantOptions = {
            ...this.selectedVariantOptions,
            [slot.handle]: { color: '', quantity: '' }
          };

          return this.selectedVariantOptions[slot.handle];
        },

        getSelectedColor(slotIndex) {
          return this.getSelectionState(slotIndex).color || '';
        },

        getSelectedQuantity(slotIndex) {
          return this.getSelectionState(slotIndex).quantity || '';
        },

        isColorDisabled(slotIndex, color) {
          const selectedQuantity = this.getSelectedQuantity(slotIndex);
          const variants = this.getParsedVariants(slotIndex);

          if (selectedQuantity) {
            const variant = variants.find((item) => item.color === color && item.quantity === selectedQuantity);
            return !variant || !variant.available;
          }

          return !variants.some((item) => item.color === color && item.available);
        },

        isQuantityDisabled(slotIndex, quantity) {
          const selectedColor = this.getSelectedColor(slotIndex);
          const variants = this.getParsedVariants(slotIndex);

          if (selectedColor) {
            const variant = variants.find((item) => item.color === selectedColor && item.quantity === quantity);
            return !variant || !variant.available;
          }

          return !variants.some((item) => item.quantity === quantity && item.available);
        },

        setSelectedColor(slotIndex, color) {
          const slot = this.compareProducts[slotIndex];
          if (!slot || !slot.handle) return;

          const current = this.getSelectionState(slotIndex);
          let quantity = current.quantity;

          const exact = this.findVariantByOptions(slotIndex, color, quantity);
          if (!exact || !exact.available) {
            const variants = this.getParsedVariants(slotIndex);
            const fallback = variants.find((item) => item.color === color && item.available)
              || variants.find((item) => item.color === color)
              || null;
            quantity = fallback ? fallback.quantity : '';
          }

          this.selectedVariantOptions = {
            ...this.selectedVariantOptions,
            [slot.handle]: { color, quantity }
          };

          this.resolveSelectedVariant(slotIndex);
        },

        setSelectedQuantity(slotIndex, quantity) {
          const slot = this.compareProducts[slotIndex];
          if (!slot || !slot.handle) return;

          const current = this.getSelectionState(slotIndex);
          let color = current.color;

          const exact = this.findVariantByOptions(slotIndex, color, quantity);
          if (!exact || !exact.available) {
            const variants = this.getParsedVariants(slotIndex);
            const fallback = variants.find((item) => item.quantity === quantity && item.available)
              || variants.find((item) => item.quantity === quantity)
              || null;
            color = fallback ? fallback.color : '';
          }

          this.selectedVariantOptions = {
            ...this.selectedVariantOptions,
            [slot.handle]: { color, quantity }
          };

          this.resolveSelectedVariant(slotIndex);
        },

        resolveSelectedVariant(slotIndex) {
          const selectedColor = this.getSelectedColor(slotIndex);
          const selectedQuantity = this.getSelectedQuantity(slotIndex);
          if (!selectedColor || !selectedQuantity) return;

          const variant = this.findVariantByOptions(slotIndex, selectedColor, selectedQuantity);
          if (!variant || !variant.available) return;

          this.selectVariant(slotIndex, variant.id);
        },

        ensureVariantSelection(slotIndex) {
          const slot = this.compareProducts[slotIndex];
          if (!slot || !slot.handle) return;

          const variants = this.getParsedVariants(slotIndex);
          if (!variants.length) return;

          const selectedVariantId = this.getSelectedVariant(slotIndex);
          if (selectedVariantId) {
            const selected = variants.find((item) => Number(item.id) === Number(selectedVariantId));
            if (selected) {
              const current = this.getSelectionState(slotIndex);
              if (current.color !== selected.color || current.quantity !== selected.quantity) {
                this.selectedVariantOptions = {
                  ...this.selectedVariantOptions,
                  [slot.handle]: {
                    color: selected.color,
                    quantity: selected.quantity
                  }
                };
              }
              return;
            }
          }

          const defaultVariant = variants.find((item) => item.available) || variants[0];
          if (!defaultVariant) return;

          this.selectedVariantOptions = {
            ...this.selectedVariantOptions,
            [slot.handle]: {
              color: defaultVariant.color,
              quantity: defaultVariant.quantity
            }
          };
          this.selectVariant(slotIndex, defaultVariant.id);
        },

        getSelectedVariant(slotIndex) {
          const slot = this.compareProducts[slotIndex];
          if (!slot || !slot.handle) return null;
          return this.selectedVariants[slot.handle] || null;
        },

        getCompareSlotKey(slot) {
          if (!slot || typeof slot !== 'object') return null;
          return slot.handle || slot.id || slot.variant_id || slot.variantId || slot.title || null;
        },

        isAddingToCart(slot) {
          const key = this.getCompareSlotKey(slot);
          return key ? !!this.addingToCart[key] : false;
        },

        setAddingToCart(slot, value) {
          const key = this.getCompareSlotKey(slot);
          if (!key) return;
          this.addingToCart = {
            ...this.addingToCart,
            [key]: !!value
          };
        },

        getProductUrl(slot) {
          if (!slot || typeof slot !== 'object') return null;
          if (slot.url) return slot.url;
          if (slot.handle) return window.Shopify.routes.root + 'products/' + slot.handle;
          return null;
        },

        getGridCardMarkup(slot) {
          if (!slot || typeof slot !== 'object' || !slot.handle || !this.$root) return '';

          const sourceCard = Array.from(this.$root.querySelectorAll('.compare-grid .compare-grid-card[data-compare-handle]'))
            .find((gridCard) => gridCard.dataset.compareHandle === slot.handle);

          if (!sourceCard) {
            return this.gridCardMarkupCache[slot.handle] || '';
          }

          const renderedCard = sourceCard.querySelector('.card-product');
          if (renderedCard) {
            this.gridCardMarkupCache = {
              ...this.gridCardMarkupCache,
              [slot.handle]: renderedCard.outerHTML
            };
          }

          return renderedCard ? renderedCard.outerHTML : (this.gridCardMarkupCache[slot.handle] || '');
        },

        getSlotImages(slot) {
          if (!slot || typeof slot !== 'object') return [];
          if (Array.isArray(slot.images) && slot.images.length) {
            return slot.images.filter(Boolean);
          }
          return slot.image ? [slot.image] : [];
        },

        getSlotPrimaryImage(slot) {
          const images = this.getSlotImages(slot);
          return images[0] || '';
        },

        getSlotSliderId(slot, slotIndex) {
          const key = this.getCompareSlotKey(slot) || slotIndex;
          return `compare-slot-slider-${key}`;
        },

        renderRatingStars(ratingValue, ratingCount) {
          const rating = parseFloat(ratingValue);
          if (!rating || Number.isNaN(rating)) return '';

          const maxStars = 5;
          const fullStars = Math.max(0, Math.min(maxStars, Math.round(rating)));
          const emptyStars = maxStars - fullStars;
          const full = '<span class="compare-star compare-star--full" aria-hidden="true">★</span>'.repeat(fullStars);
          const empty = '<span class="compare-star compare-star--empty" aria-hidden="true">★</span>'.repeat(emptyStars);
          const countText = ratingCount ? `<span class="compare-rating-count">${ratingCount} Reviews</span>` : '';

          return `<span class="compare-stars" aria-label="Rated ${rating} out of ${maxStars}">${full}${empty}</span>${countText}`;
        },

        addToCart(slot) {
          if (this.isAddingToCart(slot)) return;
          this.setAddingToCart(slot, true);

          // Try to get selected variant first, then fall back to default
          const slotIndex = this.compareProducts.findIndex((p) => this.getCompareSlotKey(p) === this.getCompareSlotKey(slot));
          const selectedVariantId = slotIndex >= 0 ? this.getSelectedVariant(slotIndex) : null;
          const initialVariantId = selectedVariantId || slot?.id || slot?.variant_id || slot?.variantId;

          const resolveVariantId = () => {
            if (initialVariantId) return Promise.resolve(Number(initialVariantId));
            if (!slot?.handle) return Promise.resolve(null);

            return fetch(window.Shopify.routes.root + 'products/' + slot.handle + '.js')
              .then((response) => response.ok ? response.json() : null)
              .then((productData) => {
                if (!productData || !Array.isArray(productData.variants) || !productData.variants.length) return null;
                const availableVariant = productData.variants.find((variant) => variant.available);
                return Number((availableVariant || productData.variants[0]).id);
              })
              .catch(() => null);
          };

          const cartHelper = Alpine.store('xCartHelper');
          const sectionsToRender = cartHelper?.getSectionsToRender?.() || [];
          resolveVariantId()
          .then((variantId) => {
            if (!variantId || Number.isNaN(variantId)) {
              throw new Error('Missing variant id for compared product.');
            }

            const addToCartRequest = {
              items: [
                {
                  id: variantId,
                  quantity: 1
                }
              ],
              sections: sectionsToRender.map((section) => section.id)
            };

            return fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(addToCartRequest)
            });
          })
          .then((response) => response.json())
          .then((data) => {
            if (data?.status === 422 || data?.status === '422') {
              throw new Error(data?.description || 'Variant is unavailable.');
            }

            if (Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
              Alpine.store('xCartNoti').setItem(data);
            } else if (Alpine.store('xMiniCart')) {
              Alpine.store('xMiniCart').openCart();
              document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
            }

            sectionsToRender.forEach((section) => {
              const sectionElement = document.querySelector(section.selector);
              if (sectionElement && data?.sections?.[section.id]) {
                if (typeof getSectionInnerHTML === 'function') {
                  sectionElement.innerHTML = getSectionInnerHTML(data.sections[section.id], section.selector);
                } else {
                  sectionElement.innerHTML = data.sections[section.id];
                }
              }
            });

            if (cartHelper) {
              const cartCountElement = document.querySelector('#cart-icon-bubble span');
              if (cartCountElement) {
                cartHelper.currentItemCount = parseInt(cartCountElement.innerHTML, 10);
              }
            }
            document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
          })
          .catch((error) => {
            console.error('Error adding to cart:', error);
          })
          .finally(() => {
            this.setAddingToCart(slot, false);
          });
        },

        isAdded(handle) {
          return this.compareProducts.some((item) => item.handle === handle);
        },

        setGridFilter(filterKey) {
          this.filterPrefetchKey += 1;
          this.filterRequestKey += 1;
          const nextFilter = (filterKey || '').toString().toLowerCase();
          const query = (this.searchQuery || '').trim();
          this.selectedGridFilter = nextFilter;
          this.visibleGridCount = this.gridStep;

          if (query) {
            this.ensureFilterCoverage();
            return;
          }

          if (!nextFilter) {
            this.restoreDefaultGridState();
            return;
          }

          this.fetchCollectionFilterGrid(nextFilter, 1, false);
        },

        wait(ms) {
          return new Promise((resolve) => {
            window.setTimeout(resolve, ms);
          });
        },

        async ensureFilterCoverage() {
          await this.ensureMatchedGridCardCount(1);
        },

        async ensureMatchedGridCardCount(targetCount) {
          const activeFilter = (this.selectedGridFilter || '').trim().toLowerCase();
          if (!activeFilter) return;

          const runKey = this.filterPrefetchKey;
          const desiredCount = Math.max(0, Number(targetCount) || 0);
          if (!desiredCount) return;

          let safety = 0;
          const maxAutoPages = 20;
          const maxIdleChecks = 80;
          let idleChecks = 0;

          while (safety < maxAutoPages) {
            if (runKey !== this.filterPrefetchKey) return;
            if (this.getMatchedGridCards().length >= desiredCount) return;

            if (this.isLoadingGridPage) {
              if (idleChecks >= maxIdleChecks) return;
              idleChecks += 1;
              await this.wait(75);
              continue;
            }

            if (!this.hasNextGridPage || !this.nextGridPage) return;

            idleChecks = 0;

            const pageToFetch = this.nextGridPage;
            let appendedCount = 0;

            if (this.activeGridMode === 'search') {
              appendedCount = await this.fetchSearchGrid(pageToFetch, true);
            } else if (this.activeGridMode === 'filter') {
              appendedCount = await this.fetchCollectionFilterGrid(activeFilter, pageToFetch, true);
            } else {
              appendedCount = await this.fetchGridPage(pageToFetch);
            }

            if (runKey !== this.filterPrefetchKey) return;
            if (!appendedCount) return;

            safety += 1;
          }
        },

        isGridFilterActive(filterKey) {
          return (this.selectedGridFilter || '') === ((filterKey || '').toString().toLowerCase());
        },

        matchesGridFilter(filterKey) {
          const activeFilter = (this.selectedGridFilter || '').trim().toLowerCase();
          if (!activeFilter) return true;
          return (filterKey || '').toString().toLowerCase() === activeFilter;
        },

        getMatchedGridCards() {
          if (!this.$root) return [];

          const gridCards = Array.from(this.$root.querySelectorAll('.compare-grid .compare-grid-card'));

          return gridCards.filter((card) => {
            const cardFilter = (card.dataset.compareFilter || '').toString().toLowerCase();
            return this.matchesGridFilter(cardFilter);
          });
        },

        shouldShowInGrid(title, handle, index, filterKey) {
          const query = (this.searchQuery || '').trim().toLowerCase();
          const productTitle = (title || '').toString().toLowerCase();
          const productHandle = (handle || '').toString().toLowerCase();
          const queryMatched = !query || this.activeGridMode === 'search' || productTitle.includes(query) || productHandle.includes(query);
          if (!queryMatched) return false;
          if (!this.matchesGridFilter(filterKey)) return false;

          if (query) return true;

          const matchedCards = this.getMatchedGridCards();
          if (!matchedCards.length) return false;

          const matchedIndex = matchedCards.findIndex((card) => {
            return (card.dataset.compareHandle || '') === (handle || '');
          });

          if (matchedIndex === -1) {
            return Number(index) < this.visibleGridCount;
          }

          return matchedIndex < this.visibleGridCount;
        },

        loadMoreGrid() {
          const activeFilter = (this.selectedGridFilter || '').trim().toLowerCase();
          const nextVisibleCount = this.visibleGridCount + this.gridStep;

          if (activeFilter) {
            const loadedMatchedCount = this.getMatchedGridCards().length;

            if (this.visibleGridCount < loadedMatchedCount) {
              this.visibleGridCount = Math.min(nextVisibleCount, loadedMatchedCount);
              return;
            }

            this.ensureMatchedGridCardCount(nextVisibleCount)
              .then(() => {
                const updatedMatchedCount = this.getMatchedGridCards().length;
                if (updatedMatchedCount <= this.visibleGridCount) return;

                this.visibleGridCount = Math.min(nextVisibleCount, updatedMatchedCount);
              });
            return;
          }

          if (this.activeGridMode === 'search') {
            if (this.isLoadingGridPage || !this.hasNextGridPage || !this.nextGridPage) {
              return;
            }

            this.fetchSearchGrid(this.nextGridPage, true);
            return;
          }

          if (this.activeGridMode === 'filter') {
            if (this.isLoadingGridPage || !this.hasNextGridPage || !this.nextGridPage) {
              return;
            }

            this.fetchCollectionFilterGrid(activeFilter, this.nextGridPage, true)
              .then((appendedCount) => {
                if (!appendedCount) return;
                this.visibleGridCount = Math.min(nextVisibleCount, this.getMatchedGridCards().length);
              });
            return;
          }

          const loadedMatchedCount = this.getMatchedGridCards().length;

          if (this.visibleGridCount < loadedMatchedCount) {
            this.visibleGridCount = Math.min(nextVisibleCount, loadedMatchedCount);
            return;
          }

          if (this.isLoadingGridPage || !this.hasNextGridPage || !this.nextGridPage) {
            return;
          }

          const requestedPage = this.nextGridPage;

          this.fetchGridPage(requestedPage)
            .then((appendedCount) => {
              if (!appendedCount) return;
              this.visibleGridCount = Math.min(nextVisibleCount, this.getMatchedGridCards().length);
            });
        },

        shouldShowLoadMoreButton() {
          return !(this.selectedGridFilter || '').trim() && this.hasMoreGridProducts();
        },

        autoLoadFilteredGrid() {
          const activeFilter = (this.selectedGridFilter || '').trim().toLowerCase();
          if (!activeFilter || !this.hasMoreGridProducts() || this.isLoadingGridPage) {
            return;
          }

          this.loadMoreGrid();
        },

        getGridCardCount() {
          if (!this.$root) return 0;
          return this.$root.querySelectorAll('.compare-grid .compare-grid-card').length;
        },

        hasMoreGridProducts() {
          const query = (this.searchQuery || '').trim();
          if (query) return this.hasNextGridPage;
          return this.visibleGridCount < this.getMatchedGridCards().length || this.hasNextGridPage;
        },

        compareSlots() {
          return Array.from({ length: 3 }, (_, index) => this.compareProducts[index] || null);
        }
      }));

      Alpine.store('xProductComparisonPopup', {
        loadTablet(el, url) {
          if(url) {
            fetch(url)
              .then(response => response.text())
              .then(text => {
                const html = document.createElement('div');
                html.innerHTML = text;
                const recommendations = html.querySelector('.product-comparison-table');
                if (recommendations && recommendations.innerHTML.trim().length) {
                  requestAnimationFrame(() => {
                    el.innerHTML = recommendations.innerHTML;
                    el.querySelectorAll('.content-tablet').forEach((item) => {
                      if (el.querySelector('.'+item.dataset.selectHtml)) {
                        el.querySelector('.'+item.dataset.selectHtml).innerHTML += item.innerHTML;
                      }
                    });
                  });
                }
              }).catch(e => {console.error(e);});
          }else {
            el.querySelectorAll('.content-tablet').forEach((item) => {
              if (el.querySelector('.'+item.dataset.selectHtml)) {
                el.querySelector('.'+item.dataset.selectHtml).innerHTML += item.innerHTML;
              }
            });
          }
        }
      });
    })
  });
}    